<script setup lang="ts">
import { IconInfoCircle, IconX } from '@tabler/icons-vue'
import { computed } from 'vue'

import BaseBadge from '@tix/ds/components/atoms/BaseBadge.vue'
import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import { isFlowSoldOut } from '@tix/entity'
import type { Schema } from '@tix/openapi-sdk'

import { formatGalleryImages } from '@/helpers/imageHelpers'

import { openBundleDetailsModal } from '@/components/bundles/openDescriptionModal'

type Props = {
  name: Schema<'flow_definition_min'>['name']
  meta: Schema<'flow_definition_min'>['meta']
}

const props = defineProps<Props>()

const image = computed(() => formatGalleryImages(props.meta.images)?.[0] ?? null)
const ariaLabel = computed(() => `View details for ${props.name}`)
const soldOut = computed(() => isFlowSoldOut(props.meta))
</script>

<template>
  <div class="bundle-header" @click="openBundleDetailsModal({ name, meta })">
    <img v-if="image" :alt="image.alt" class="bundle-header-image" height="32" :src="image.src" width="32" />

    <div class="bundle-header-title-container">
      <h1 class="bundle-header-title h3">{{ name }}</h1>

      <BaseBadge
        v-if="soldOut"
        class="bundle-header-sold-out-badge"
        :iconBefore="IconX"
        label="Sold Out"
        type="danger"
        variant="filled"
      />
    </div>

    <BaseButton
      :aria-label="ariaLabel"
      class="bundle-header-info-button"
      hierarchy="tertiary"
      :iconAfter="IconInfoCircle"
      :title="ariaLabel"
      @click.stop="openBundleDetailsModal({ name, meta })"
    />
  </div>
</template>

<style scoped>
.bundle-header {
  display: flex;
  align-items: center;
  gap: var(--space-0-75);
  cursor: pointer;

  &:has(.bundle-header-sold-out-badge) {
    align-items: flex-start;

    .bundle-header-info-button {
      align-items: flex-start;
    }
  }

  .bundle-header-image {
    width: var(--space-2);
    height: var(--space-2);
    object-fit: cover;
    object-position: center;
    border-radius: var(--radius-small);
  }

  .bundle-header-title-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 0;

    .bundle-header-title {
      flex-grow: 1;
    }

    .bundle-header-sold-out-badge {
      width: fit-content;
      font-size: var(--size-3xs);
    }
  }

  .bundle-header-info-button {
    display: flex;
    align-items: center;
    width: var(--touch-target-height);
    height: 100%;
  }

  @media (--desktop-experience) {
    .bundle-header-image {
      width: var(--space-3);
      height: var(--space-3);
    }

    .bundle-header-title-container {
      .bundle-header-sold-out-badge {
        width: fit-content;
        font-size: var(--size-xs);
      }
    }
  }
}
</style>
